import api from './api';

const organizationService = {
  // Get current user's organization
  async getCurrentOrganization() {
    try {
      const response = await api.get('/organizations/current');
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch current organization');
    }
  },

  // Get all organizations (platform admin only)
  async getAllOrganizations(page = 1, limit = 10, search = '') {
    try {
      const params = { page, limit };
      if (search) params.search = search;
      
      const response = await api.get('/organizations/all', { params });
      return {
        organizations: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch organizations');
    }
  },

  // Get specific organization by ID
  async getOrganization(id) {
    try {
      const response = await api.get(`/organizations/${id}`);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch organization');
    }
  },

  // Create new organization (platform admin only)
  async createOrganization(orgData) {
    try {
      const response = await api.post('/organizations', orgData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create organization');
    }
  },

  // Update organization
  async updateOrganization(id, updates) {
    try {
      const response = await api.put(`/organizations/${id}`, updates);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to update organization');
    }
  },

  // Delete organization (platform admin only)
  async deleteOrganization(id) {
    try {
      await api.delete(`/organizations/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to delete organization');
    }
  },

  // Get organization departments
  async getDepartments(organizationId) {
    try {
      const response = await api.get(`/organizations/${organizationId}/departments`);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch departments');
    }
  },

  // Create new department
  async createDepartment(deptData) {
    try {
      const response = await api.post('/organizations/departments', deptData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create department');
    }
  },

  // Update department
  async updateDepartment(id, updates) {
    try {
      const response = await api.put(`/organizations/departments/${id}`, updates);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to update department');
    }
  },

  // Delete department
  async deleteDepartment(id) {
    try {
      await api.delete(`/organizations/departments/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to delete department');
    }
  },

  // Get organization users
  async getOrganizationUsers(params = {}) {
    try {
      const { page = 1, limit = 100, search = '', role = '', department = '', status = '', purpose = '' } = params;
      const queryParams = { page, limit };
      if (search) queryParams.search = search;
      if (role && role !== 'all') queryParams.role = role;
      if (department && department !== 'all') queryParams.department = department;
      if (status && status !== 'all') queryParams.status = status;
      if (purpose) queryParams.purpose = purpose;
      
      const response = await api.get('/organizations/users', { params: queryParams });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch organization users');
    }
  },

  // Invite user to organization
  async inviteUser(invitationData) {
    try {
      const response = await api.post('/organizations/users/invite', invitationData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send invitation');
    }
  },

  // Update user role in organization
  async updateUserRole(userId, roleData) {
    try {
      const response = await api.patch(`/organizations/users/${userId}/role`, roleData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to update user role');
    }
  },

  // Remove user from organization
  async removeUser(userId) {
    try {
      await api.delete(`/organizations/users/${userId}`);
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to remove user');
    }
  },

  // Get organization statistics
  async getOrganizationStats(organizationId) {
    try {
      const response = await api.get(`/organizations/${organizationId}/stats`);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch organization statistics');
    }
  },

  // Get organization dashboard statistics
  async getOrganizationDashboardStats() {
    try {
      const response = await api.get('/organizations/dashboard/stats');
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch organization dashboard statistics');
    }
  },

  // Check domain availability
  async checkDomainAvailability(domain) {
    try {
      const response = await api.post('/organizations/check-domain', { domain });
      return response.data.available;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to check domain availability');
    }
  },

  // Check subdomain availability
  async checkSubdomainAvailability(subdomain) {
    try {
      const response = await api.post('/organizations/check-subdomain', { subdomain });
      return response.data.available;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to check subdomain availability');
    }
  },

  // Get organization billing information
  async getBillingInfo(organizationId) {
    try {
      const response = await api.get(`/organizations/${organizationId}/billing`);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch billing information');
    }
  },

  // Update billing information
  async updateBilling(organizationId, billingData) {
    try {
      const response = await api.put(`/organizations/${organizationId}/billing`, billingData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to update billing information');
    }
  },

  // ===== Permission Management =====
  
  // Get available permission templates
  async getPermissionTemplates() {
    try {
      const response = await api.get('/organizations/permissions/templates');
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch permission templates');
    }
  },

  // Get permissions for a specific role
  async getPermissionsForRole(role) {
    try {
      const response = await api.get(`/organizations/permissions/roles/${role}`);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch role permissions');
    }
  },

  // Get user's effective permissions
  async getUserPermissions(userId) {
    try {
      const response = await api.get(`/organizations/users/${userId}/permissions`);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch user permissions');
    }
  },

  // Update user's custom permissions
  async updateUserPermissions(userId, permissionsData) {
    try {
      const response = await api.patch(`/organizations/users/${userId}/permissions`, permissionsData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to update user permissions');
    }
  }
};

export default organizationService;
